import { useState, useEffect, useCallback } from 'react'
import { Rocket, RefreshCw, Search, ExternalLink, DollarSign } from 'lucide-react'
import { clsx } from 'clsx'
import { formatDistanceToNow } from 'date-fns'
import { api } from '../lib/api'

const SECTORS = [
  { value: 'all', label: 'All Sectors' },
  { value: 'ai', label: 'AI' },
  { value: 'fintech', label: 'Fintech' },
  { value: 'healthtech', label: 'Healthtech' },
  { value: 'cybersecurity', label: 'Cybersecurity' },
  { value: 'climate', label: 'Climate' },
  { value: 'saas', label: 'SaaS' },
  { value: 'devtools', label: 'DevTools' },
]

const SECTOR_BADGE: Record<string, string> = {
  ai: 'badge-purple',
  fintech: 'badge-blue',
  healthtech: 'badge-green',
  cybersecurity: 'badge-red',
  climate: 'badge-green',
  saas: 'badge-cyan',
  devtools: 'badge-amber',
}

const STAGE_BADGE: Record<string, string> = {
  seed: 'badge-green',
  series_a: 'badge-blue',
  series_b: 'badge-cyan',
  series_c_plus: 'badge-purple',
  ipo: 'badge-amber',
  acquisition: 'badge-red',
  product_launch: 'badge-green',
}

function relativeTime(ts: string): string {
  if (!ts) return ''
  try { return formatDistanceToNow(new Date(ts), { addSuffix: false }) + ' ago' } catch { return ts }
}

function formatAmount(amount: string | number | undefined): string {
  if (!amount) return ''
  const num = typeof amount === 'string' ? parseFloat(amount) : amount
  if (isNaN(num)) return String(amount)
  if (num >= 1_000_000_000) return `$${(num / 1_000_000_000).toFixed(1)}B`
  if (num >= 1_000_000) return `$${(num / 1_000_000).toFixed(1)}M`
  if (num >= 1_000) return `$${(num / 1_000).toFixed(0)}K`
  return `$${num}`
}

export default function StartupsPage() {
  const [items, setItems] = useState<any[]>([])
  const [sectorFilter, setSectorFilter] = useState('all')
  const [searchTerm, setSearchTerm] = useState('')
  const [loading, setLoading] = useState(true)

  const fetchData = useCallback(async () => {
    setLoading(true)
    try {
      const params: Record<string, any> = { limit: 50 }
      if (sectorFilter !== 'all') params.sector = sectorFilter
      const { data } = await api.get('/api/startups/feed', { params })
      setItems(data.items || [])
    } finally {
      setLoading(false)
    }
  }, [sectorFilter])

  useEffect(() => { fetchData(); const i = setInterval(fetchData, 120_000); return () => clearInterval(i) }, [fetchData])

  const filtered = items.filter(item => {
    if (searchTerm) {
      const q = searchTerm.toLowerCase()
      if (
        !(item.title || '').toLowerCase().includes(q) &&
        !(item.summary || '').toLowerCase().includes(q)
      ) return false
    }
    return true
  })

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-text-primary flex items-center gap-3">
            <Rocket size={24} className="text-amber" /> Startups &amp; VC Intelligence
          </h1>
          <p className="text-sm text-text-secondary mt-1">Funding rounds and launches from Crunchbase, TechCrunch, PitchBook, and AngelList</p>
        </div>
        <button type="button" onClick={fetchData} className="btn-secondary text-xs">
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} /> Refresh
        </button>
      </div>

      <div className="flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted" />
          <input value={searchTerm} onChange={e => setSearchTerm(e.target.value)} placeholder="Search startups..." className="input pl-9 py-2 text-xs" />
        </div>
        <select title="Filter by sector" value={sectorFilter} onChange={e => setSectorFilter(e.target.value)} className="input w-auto py-2 text-xs">
          {SECTORS.map(s => (
            <option key={s.value} value={s.value}>{s.label}</option>
          ))}
        </select>
      </div>

      {loading && !items.length && (
        <div className="flex items-center justify-center h-32">
          <RefreshCw size={20} className="animate-spin text-text-muted" />
          <span className="ml-2 text-text-muted text-sm">Fetching startup feed...</span>
        </div>
      )}

      <div className="space-y-3">
        {filtered.map((item, idx) => (
          <div key={item.id || idx} className="card card-hover p-4">
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  {item.sector && (
                    <span className={clsx('text-[10px]', SECTOR_BADGE[item.sector] || 'badge-blue')}>{item.sector}</span>
                  )}
                  {item.stage && (
                    <span className={clsx('text-[10px]', STAGE_BADGE[item.stage] || 'badge-blue')}>{item.stage.replace(/_/g, ' ')}</span>
                  )}
                  {item.amount && (
                    <span className="badge-green text-[10px]"><DollarSign size={10} /> {formatAmount(item.amount)}</span>
                  )}
                </div>
                <h3 className="text-sm font-semibold text-text-primary leading-snug">
                  {item.url ? (
                    <a href={item.url} target="_blank" rel="noopener" className="hover:underline">{item.title}</a>
                  ) : item.title}
                </h3>
                <p className="text-xs text-text-secondary mt-1 leading-relaxed line-clamp-2">{item.summary}</p>
                <div className="flex items-center gap-2 mt-2 text-[10px] text-text-muted">
                  <span>{item.source}</span>
                  <span>&middot;</span>
                  <span>{relativeTime(item.timestamp)}</span>
                </div>
              </div>
              {item.url && (
                <a href={item.url} target="_blank" rel="noopener" className="text-text-muted hover:text-text-primary shrink-0">
                  <ExternalLink size={14} />
                </a>
              )}
            </div>
          </div>
        ))}
        {!loading && filtered.length === 0 && (
          <p className="text-xs text-text-muted text-center py-8">No items match your current filters</p>
        )}
      </div>
    </div>
  )
}
